// ecommerce-frontend/src/pages/ProfileEditPage.jsx
// ✅ VERSION FINALE: Cropping photo de profil + UX améliorée (Glassmorphism, Animations)

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Cropper from 'react-easy-crop'; // Import du composant de rognage
import { 
  User, Mail, Phone, MapPin, Lock, Save, ArrowLeft, AlertCircle, CheckCircle, 
  Home, Camera, Eye, EyeOff, Trash2, PauseCircle, Crop, // <-- Crop est déjà là
  Check // <-- C'est l'ajout qui résout l'erreur !
} from 'lucide-react';
import { authAPI } from '../services/api';

// --------------------------------------------------------------------------
// FONCTIONS UTILITAIRES POUR LE CROPPING (hors composant pour la performance)
// --------------------------------------------------------------------------

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    // Pour des raisons de sécurité du navigateur, le crossOrigin doit être défini
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const { width: cropWidth, height: cropHeight } = pixelCrop;

  canvas.width = cropWidth;
  canvas.height = cropHeight;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return new Promise((resolve) => {
    // Crée un Blob (le fichier) à partir du canvas
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9); // Format et qualité de l'image
  });
}

// --------------------------------------------------------------------------
// COMPOSANT PRINCIPAL
// --------------------------------------------------------------------------

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const user = authAPI.getCurrentUser();
  
  // 1. États du formulaire de profil
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
  });
  
  // 2. États pour la photo de profil
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null); // Fichier final (Blob/File) après rognage
  const [photoPreview, setPhotoPreview] = useState(user?.profile_photo || null); // URL de prévisualisation (avant/après rognage)
  const [imageToCrop, setImageToCrop] = useState(null); // Source Base64 pour le Cropper (active le mode rognage)
  
  // 3. États pour le Cropper
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null); // Zone finale rognée

  // 4. États du mot de passe
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });
  
  // 5. États de l'UI
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);

  // LOGIQUE DE MISE À JOUR DU PROFIL -----------------------------------------

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  // -------------------------------------------------------------------------
  // GESTION DU CROPPING
  // -------------------------------------------------------------------------

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ photo: 'La taille de l\'image ne doit pas dépasser 5 Mo' });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setErrors({ photo: 'Veuillez sélectionner une image valide' });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        // Active le mode Cropper avec la source Base64
        setImageToCrop(reader.result); 
        setPhotoPreview(reader.result); // Aperçu dans le cropper
        setProfilePhoto(null); // Réinitialise le fichier final en attendant le crop
        setActiveTab('photo'); // Bascule sur l'onglet photo
      };
      reader.readAsDataURL(file);
      setErrors({ ...errors, photo: '' });
    }
  };

  // Callback appelé lorsque l'utilisateur change la zone de rognage
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Fonction pour finaliser le rognage et préparer le fichier pour l'upload
  const handleCropDone = useCallback(async () => {
    if (!croppedAreaPixels || !imageToCrop) return;
    
    setLoading(true);
    try {
      // 1. Génère le Blob (fichier) rogné
      const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
      
      // 2. Convertir le Blob en File (pour l'upload via FormData)
      const fileType = croppedBlob.type || 'image/jpeg';
      const croppedFile = new File([croppedBlob], `profile-${user.username}.${fileType.split('/')[1] || 'jpeg'}`, { type: fileType });

      // 3. Mettre à jour les états pour l'aperçu et l'upload
      setPhotoPreview(URL.createObjectURL(croppedFile));
      setProfilePhoto(croppedFile);
      
      // 4. Quitter le mode rognage
      setImageToCrop(null); 
      setSuccess('Photo rognée et prête à être enregistrée.');

    } catch (e) {
      console.error('Erreur de rognage:', e);
      setErrors({ photo: 'Erreur lors du rognage de l\'image.' });
    } finally {
      setLoading(false);
    }
  }, [imageToCrop, croppedAreaPixels, user]);

  // Fonction pour annuler le rognage
  const handleCropCancel = useCallback(() => {
      setImageToCrop(null); 
      // Réinitialiser la prévisualisation à l'image stockée ou à null
      setPhotoPreview(user?.profile_photo || null);
      setProfilePhoto(null);
  }, [user]);

  // -------------------------------------------------------------------------
  // FIN GESTION DU CROPPING
  // -------------------------------------------------------------------------

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('username', formData.username);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('address', formData.address);
      
      // La photo est ajoutée SEULEMENT si c'est un nouveau fichier (résultat du crop)
      if (profilePhoto && profilePhoto instanceof File) {
        formDataToSend.append('profile_photo', profilePhoto);
      } else if (profilePhoto === null && user.profile_photo !== null && activeTab === 'photo') {
         // Logique pour supprimer la photo si nécessaire (non implémenté côté API)
      }


      await authAPI.updateProfile(formDataToSend);
      
      // Mise à jour de l'utilisateur dans le local storage
      const updatedUser = { 
        ...user, 
        ...formData, 
        // Si profilePhoto est un objet File, on utilise sa preview URL, sinon on garde l'ancienne.
        profile_photo: profilePhoto instanceof File ? photoPreview : (profilePhoto || user.profile_photo) 
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setSuccess('Profil mis à jour avec succès !');
      
      setTimeout(() => {
        const dashboardPath = user?.user_type === 'vendeur' ? '/vendor/dashboard' : '/buyer/dashboard';
        navigate(dashboardPath);
      }, 2000);

    } catch (err) {
      console.error('Erreur de mise à jour:', err);
      if (err.response && err.response.data) {
        setErrors(err.response.data);
      } else {
        setErrors({ general: 'Erreur lors de la mise à jour du profil' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess('');

    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrors({ confirm_password: 'Les mots de passe ne correspondent pas' });
      return;
    }

    if (passwordData.new_password.length < 8) {
      setErrors({ new_password: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      
      setSuccess('Mot de passe changé avec succès !');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      
      setTimeout(() => {
        authAPI.logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      if (err.response && err.response.data) {
        setErrors(err.response.data);
      } else {
        setErrors({ general: 'Erreur lors du changement de mot de passe' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendAccount = async () => {
    setLoading(true);
    try {
      await authAPI.suspendAccount();
      setSuccess('Compte suspendu avec succès');
      setTimeout(() => {
        authAPI.logout();
        navigate('/login');
      }, 2000);
    } catch (err) {
      setErrors({ general: 'Erreur lors de la suspension du compte' });
    } finally {
      setLoading(false);
      setShowSuspendModal(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await authAPI.deleteAccount();
      setSuccess('Compte supprimé avec succès');
      setTimeout(() => {
        authAPI.logout();
        navigate('/');
      }, 2000);
    } catch (err) {
      setErrors({ general: 'Erreur lors de la suppression du compte' });
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  // --------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated Background - HARMONISÉ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Bulle orange/rose */}
        <div className="absolute top-0 -left-20 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        {/* Bulle mauve/bleu */}
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-gradient-to-tl from-purple-500/15 to-blue-500/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        {/* Bulle centrale */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '500ms' }}></div>
      </div>

      {/* Header Glass - HARMONISÉ (max-w-7xl) */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-white/5 border-b border-white/10 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Bouton Retour */}
              <button 
                onClick={() => navigate(-1)}
                className="group p-2.5 text-white/70 hover:text-purple-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
                title="Retour"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <ArrowLeft size={20} className="relative z-10" />
              </button>
              {/* Logo MonMarket harmonisé */}
              <div 
                className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-orange-400 via-orange-500 to-orange-600 bg-clip-text text-transparent cursor-pointer hover:scale-105 transition-transform"
                onClick={() => navigate('/')}
              >
                MonMarket
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Bouton Accueil harmonisé (toujours visible) */}
              <button 
                onClick={() => navigate('/')}  
                className="group p-2.5 text-white/70 hover:text-orange-400 hover:bg-white/10 rounded-xl transition-all relative overflow-hidden"
                title="Accueil"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 to-orange-500/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <Home size={20} className="relative z-10" />
              </button>
            </div>
          </div>
        </div>
      </header>


      <main className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden border border-white/20">
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 p-6 text-white">
            <h1 className="text-3xl font-bold">Modifier mon profil</h1>
            <p className="text-orange-100 mt-2">Gérez vos informations personnelles</p>
          </div>

          {/* Tabs */}
          <div className="border-b border-white/10">
            <div className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-6 py-4 font-medium transition whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'border-b-2 border-orange-500 text-orange-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Informations personnelles
              </button>
              <button
                onClick={() => setActiveTab('photo')}
                className={`px-6 py-4 font-medium transition whitespace-nowrap ${
                  activeTab === 'photo'
                    ? 'border-b-2 border-orange-500 text-orange-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Photo de profil
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`px-6 py-4 font-medium transition whitespace-nowrap ${
                  activeTab === 'password'
                    ? 'border-b-2 border-orange-500 text-orange-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Mot de passe
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`px-6 py-4 font-medium transition whitespace-nowrap ${
                  activeTab === 'account'
                    ? 'border-b-2 border-orange-500 text-orange-400'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Gestion du compte
              </button>
            </div>
          </div>

          {/* Contenu des onglets avec transition (simule le mouvement) */}
          <div 
            key={activeTab} // La clé force le re-render et déclenche la transition CSS
            className="p-6 transition-opacity duration-500 ease-in-out animate-fadeIn" 
          >
            {success && (
              <div className="bg-green-500/20 backdrop-blur-md border border-green-400/30 rounded-2xl p-4 mb-6 flex items-center">
                <CheckCircle size={20} className="text-green-300 mr-2" />
                <span className="text-green-200">{success}</span>
              </div>
            )}

            {errors.general && (
              <div className="bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl p-4 mb-6 flex items-center">
                <AlertCircle size={20} className="text-red-300 mr-2" />
                <span className="text-red-200">{errors.general}</span>
              </div>
            )}

            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                {/* ... (champs du profil: Nom, Email, Téléphone, Adresse) ... */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom d'utilisateur
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.username ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Votre nom d'utilisateur"
                    />
                  </div>
                  {errors.username && (
                    <p className="text-red-400 text-sm mt-1">{errors.username}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.email ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="votre.email@exemple.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Téléphone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.phone ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="+225 07 12 34 56 78"
                    />
                  </div>
                  {errors.phone && (
                    <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Adresse
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-gray-400" size={20} />
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                      className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500"
                      placeholder="Votre adresse complète"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 flex items-center justify-center shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save size={20} className="mr-2" />
                      Enregistrer les modifications
                    </>
                  )}
                </button>
              </form>
            )}

            {activeTab === 'photo' && (
              <div className="space-y-6">
                
                {/* 1. Affichage du Cropper si une image est sélectionnée */}
                {imageToCrop ? (
                  <div className="space-y-4">
                    <div className="bg-blue-500/20 backdrop-blur-md border border-blue-400/30 rounded-2xl p-4">
                      <p className="text-sm text-blue-200">
                        Ajustez la zone de rognage. La photo de profil sera en format rond.
                      </p>
                    </div>
                    {/* Zone de Cropper */}
                    <div className="relative w-full h-80 bg-black rounded-xl overflow-hidden border border-white/20">
                      <Cropper
                        image={imageToCrop}
                        crop={crop}
                        zoom={zoom}
                        aspect={1 / 1} 
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        cropShape="round" // Rend la zone de rognage ronde
                        objectFit="contain"
                        showGrid={false}
                        style={{ 
                          containerStyle: { background: '#000' }
                        }}
                      />
                    </div>
                    
                    {/* Contrôle du Zoom */}
                    <div className="flex items-center space-x-3 p-3 bg-white/5 rounded-xl">
                      <Crop size={18} className="text-orange-400" />
                      <span className="text-gray-300 w-10 text-sm">Zoom</span>
                      <input
                        type="range"
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer range-lg focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all"
                      />
                    </div>

                    {/* Boutons d'action du Cropper */}
                    <div className="flex space-x-4 pt-2">
                      <button
                        onClick={handleCropCancel}
                        className="flex-1 flex items-center justify-center space-x-2 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition disabled:opacity-50"
                        disabled={loading}
                      >
                        <ArrowLeft size={20} />
                        <span>Annuler</span>
                      </button>
                      <button
                        onClick={handleCropDone}
                        disabled={loading || !croppedAreaPixels}
                        className="flex-1 flex items-center justify-center space-x-2 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition disabled:opacity-50 shadow-xl"
                      >
                        {loading ? (
                          'Traitement...'
                        ) : (
                          <>
                            <Check size={20} />
                            <span>Valider le rognage</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                ) : (
                  
                  // 2. Affichage de la prévisualisation et du sélecteur de fichier
                  <div className="flex flex-col items-center">
                    <div className="relative">
                      <div className="w-40 h-40 rounded-full bg-white/10 backdrop-blur-md overflow-hidden border-4 border-orange-500 shadow-2xl">
                        {photoPreview ? (
                          <img 
                            src={photoPreview} 
                            alt="Photo de profil" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User size={80} className="text-gray-500" />
                          </div>
                        )}
                      </div>
                      {/* Bouton de sélection de fichier */}
                      <label 
                        htmlFor="photo-upload"
                        className="absolute bottom-0 right-0 bg-gradient-to-r from-orange-500 to-orange-600 p-3 rounded-full cursor-pointer hover:from-orange-600 hover:to-orange-700 transition shadow-xl"
                      >
                        <Camera size={20} className="text-white" />
                      </label>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        // L'input est caché car le label sert de bouton
                        className="hidden" 
                      />
                    </div>
                    
                    {errors.photo && (
                      <p className="text-red-400 text-sm mt-4">{errors.photo}</p>
                    )}
                    
                    <div className="mt-6 text-center">
                      <p className="text-sm text-gray-300">
                        Cliquez sur l'icône de l'appareil photo pour sélectionner une image à rogner.
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        Formats acceptés : JPG, PNG, GIF (Max. 5 Mo)
                      </p>
                    </div>
                    
                    {/* Bouton d'enregistrement final */}
                    <button
                      onClick={handleProfileSubmit}
                      // Le bouton est actif uniquement si un nouveau fichier (de type File) est prêt
                      disabled={loading || !(profilePhoto instanceof File)} 
                      className="w-full mt-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Enregistrement...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="mr-2" />
                          Enregistrer la photo
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div className="bg-blue-500/20 backdrop-blur-md border border-blue-400/30 rounded-2xl p-4 mb-6">
                  <p className="text-sm text-blue-200">
                    <strong>Note :</strong> Après avoir changé votre mot de passe, vous serez
                    déconnecté et devrez vous reconnecter avec votre nouveau mot de passe.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Mot de passe actuel
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type={showPasswords.old_password ? 'text' : 'password'}
                      name="old_password"
                      value={passwordData.old_password}
                      onChange={handlePasswordChange}
                      className={`w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.old_password ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Votre mot de passe actuel"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('old_password')}
                      className="absolute right-3 top-3 text-gray-400 hover:text-white"
                    >
                      {showPasswords.old_password ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.old_password && (
                    <p className="text-red-400 text-sm mt-1">{errors.old_password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type={showPasswords.new_password ? 'text' : 'password'}
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      className={`w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.new_password ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Nouveau mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new_password')}
                      className="absolute right-3 top-3 text-gray-400 hover:text-white"
                    >
                      {showPasswords.new_password ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.new_password && (
                    <p className="text-red-400 text-sm mt-1">{errors.new_password}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Minimum 8 caractères, avec majuscules, minuscules et chiffres
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Confirmer le nouveau mot de passe
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 text-gray-400" size={20} />
                    <input
                      type={showPasswords.confirm_password ? 'text' : 'password'}
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      className={`w-full pl-10 pr-12 py-3 bg-white/10 backdrop-blur-md border rounded-2xl focus:ring-2 focus:ring-orange-400 text-white placeholder-gray-500 ${
                        errors.confirm_password ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Confirmer le mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm_password')}
                      className="absolute right-3 top-3 text-gray-400 hover:text-white"
                    >
                      {showPasswords.confirm_password ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="text-red-400 text-sm mt-1">{errors.confirm_password}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-2xl font-semibold hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 flex items-center justify-center shadow-xl hover:shadow-2xl transform hover:scale-[1.02]"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Changement...
                    </>
                  ) : (
                    <>
                      <Lock size={20} className="mr-2" />
                      Changer le mot de passe
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Section "Gestion du compte" corrigée et séparée */}
            {activeTab === 'account' && (
              <div className="space-y-6">
                <div className="bg-yellow-500/20 backdrop-blur-md border border-yellow-400/30 rounded-2xl p-4">
                  <p className="text-sm text-yellow-200">
                    <strong>Attention :</strong> Ces actions affectent l'état de votre compte.
                  </p>
                </div>

                {/* Suspendre le compte */}
                <div className="bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-yellow-500/20 backdrop-blur-md rounded-xl border border-yellow-400/30">
                      <PauseCircle size={24} className="text-yellow-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">Suspendre mon compte</h3>
                      <p className="text-sm text-gray-400 mt-2">
                        Désactivez temporairement votre compte. Vous pourrez le réactiver en vous reconnectant.
                      </p>
                      <button
                        onClick={() => setShowSuspendModal(true)}
                        className="mt-4 px-4 py-2 bg-yellow-500/20 backdrop-blur-md text-yellow-400 border border-yellow-400/30 rounded-xl hover:bg-yellow-500/30 transition flex items-center"
                      >
                        <PauseCircle size={18} className="mr-2" />
                        Suspendre mon compte
                      </button>
                    </div>
                  </div>
                </div>

                {/* Supprimer le compte */}
                <div className="bg-white/5 backdrop-blur-md border border-white/20 rounded-2xl p-6">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-red-500/20 backdrop-blur-md rounded-xl border border-red-400/30">
                      <Trash2 size={24} className="text-red-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white">Supprimer mon compte</h3>
                      <p className="text-sm text-gray-400 mt-2">
                        Supprimez définitivement votre compte et toutes vos données. Cette action est irréversible.
                      </p>
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="mt-4 px-4 py-2 bg-red-500/20 backdrop-blur-md text-red-400 border border-red-400/30 rounded-xl hover:bg-red-500/30 transition flex items-center"
                      >
                        <Trash2 size={18} className="mr-2" />
                        Supprimer mon compte
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal de confirmation de suspension */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl max-w-md w-full p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 backdrop-blur-md rounded-full mx-auto mb-4 border border-yellow-400/30">
              <PauseCircle size={24} className="text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2 text-white">Suspendre le compte ?</h3>
            <p className="text-gray-300 text-center mb-6">
              Votre compte sera temporairement désactivé. Vous pourrez le réactiver en vous reconnectant.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowSuspendModal(false)}
                className="flex-1 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-gray-300 rounded-xl hover:bg-white/20 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleSuspendAccount}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-xl hover:from-yellow-600 hover:to-yellow-700 transition disabled:opacity-50 shadow-xl"
              >
                {loading ? 'Suspension...' : 'Suspendre'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl max-w-md w-full p-6 border border-white/20 shadow-2xl">
            <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 backdrop-blur-md rounded-full mx-auto mb-4 border border-red-400/30">
              <Trash2 size={24} className="text-red-400" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2 text-white">Supprimer le compte ?</h3>
            <p className="text-gray-300 text-center mb-6">
              Cette action est irréversible. Toutes vos données seront définitivement supprimées.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 text-gray-300 rounded-xl hover:bg-white/20 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition disabled:opacity-50 shadow-xl"
              >
                {loading ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}